import React, { FunctionComponent, useState } from 'react';
import Button from '$/components/ui/Button/index';
import {
  MemberFragment,
  UserRole,
  InvitationStatus,
  MeFragment,
  MyRole,
} from '$/GraphQL/schema/schema';
import ApolloForm from '../../../../components/ApolloForm';

import { DropdownOption } from '$/sections/Home/Profile/TeamManager/TeamManager';
import { css } from '@emotion/react';
import CirclePicture from '../../../../components/ui/CirclePicture';
import { DeleteIcon } from '$/images/Icons';
import Loading from '../../../../components/Loading';
import Dropdown from '$/components/ui/Input/Basics/Dropdown';
import pluralize from 'pluralize';
import Text from '$/components/ui/Text';
import Form from '$/components/ui/Form';

interface MemberProps {
  member: MemberFragment;
  changeRoleUser: (member: MemberFragment, role: UserRole) => void;
  openEditMember: (member: MemberFragment) => void;
  resendInvitationEmail: (id: string, teamId: string) => void;
  removeMember: (member: MemberFragment) => void;
  dropdownRole: DropdownOption[];
  isLoading?: boolean | false;
  me: MeFragment;
  roleChangedLoading?: boolean | false;
}

export const TeamMember: FunctionComponent<MemberProps> = ({
  member,
  changeRoleUser,
  openEditMember,
  resendInvitationEmail,
  removeMember,
  dropdownRole,
  isLoading,
  me,
  roleChangedLoading,
}) => {
  const [rowActive, setRowActive] = useState('');

  React.useEffect(() => {
    if (!isLoading) {
      setRowActive('');
    }
  }, [isLoading]);

  React.useEffect(() => {
    if (!roleChangedLoading) {
      setRowActive('');
    }
  }, [roleChangedLoading]);

  const isPending = member.invitationStatus === InvitationStatus.Pending ? true : false;

  let statusText = '';
  if (member.invitationStatus === null || member.invitationStatus === InvitationStatus.Accepted) {
    statusText = 'Active';
  }
  if (member.invitationStatus === InvitationStatus.Pending) {
    statusText = 'Pending';
  }
  const isDisabled = [MyRole.Admin, MyRole.Ops, MyRole.Tester].includes(me.inTeam.role);

  return (
    <div
      className='hover-row flex justify-between items-center py-2 border-b-2 border-gray-200 hover:bg-gray-200 cursor-pointer rounded-xl'
      onClick={(): void => {
        openEditMember(member);
      }}
    >
      <div className='pl-1/2e w-3/12 flex flex-no-wrap items-center'>
        <CirclePicture pictureRef={member.profileMediaRef} />
        <Text className={`text-teal-6 mx-2 ${isPending && 'opacity-60'}`}>
          {[member.firstName, member.lastName].join(' ')}
        </Text>
      </div>
      <div className='w-3/12 flex flex-no-wrap items-center'>
        <Text className={`text-teal-6 mx-2 text-ellipsis ${isPending && 'opacity-60'}`}>
          {member.role === UserRole.Presenter ? member.displayEmail : member.email}
        </Text>
      </div>
      <div className='w-2/12 flex items-center mr-3'>
        {roleChangedLoading && rowActive === member.id ? (
          <Loading local className='w-1_5e h-1_5e self-start' />
        ) : (
          <ApolloForm defaultValues={{ role: member.role }} className='w-full'>
            <Form.Field
              name='role'
              className={`row-dropdown my-0`}
              component={
                <Dropdown
                  options={dropdownRole}
                  disabled={!isDisabled ? true : false}
                  placeholder={'Select role'}
                />
              }
              onChange={async (value: UserRole): Promise<void> => {
                if (value === UserRole.Presenter && !member.displayEmail) {
                  openEditMember({
                    ...member,
                    role: value,
                  });
                  return;
                }
                if (member.role === UserRole.Presenter) {
                  openEditMember({
                    ...member,
                    role: value,
                    email: '',
                  });
                  return;
                }
                changeRoleUser(member, value);
                setRowActive(member.id);
              }}
            />
          </ApolloForm>
        )}
      </div>
      <div className='w-4/12 relative flex items-center pl-2'>
        <div
          className={`text-teal-6 ${
            member.invitationStatus === InvitationStatus.Pending && 'opacity-60'
          }`}
          css={css`
            padding-top: 0.785714em;
            padding-bottom: 0.785714em;
          `}
        >
          {statusText}{' '}
          {member.assignedSets &&
            member.assignedSets.length > 0 &&
            `on ${pluralize('webinar', member.assignedSets.length, true)}`}
        </div>

        <div className='flex absolute top-0 bottom-0 right-0 items-center'>
          {isPending ? (
            <Button.row
              className='mr-1e'
              submitting={isLoading && rowActive === member.id}
              onClick={(e): void => {
                e.stopPropagation();
                setRowActive(member.id);
                resendInvitationEmail(member.id, member.team.id);
              }}
            >
              Resend invite
            </Button.row>
          ) : (
            <div className='flex flex-no-wrap w-32 space-between'>&nbsp;</div>
          )}
          <Button.row
            className='mr-1/2e'
            onClick={(e): void => {
              e.stopPropagation();
              removeMember(member);
            }}
          >
            <DeleteIcon />
          </Button.row>
        </div>
      </div>
    </div>
  );
};

export default TeamMember;
